(ns hai.admin-ui.users.contacts.form
  "Shared 'add a contact' form. Used by:
   1. The sidebar `:contacts/new` route page (My Workspace > Contacts > New).
   2. The timeline filter `+` action that converts an auto-discovered virtual
      contact into a real one.

   Both callers pass their own `:initial-value` and `:on-success-fx`. The form
   submits via the `'contact/add-person` Pathom mutation for new contacts, or
   `'contact/update` when `:contact/uuid` is present in `:initial-value`."
  (:require
   [clojure.set :as set]
   [hai.ui.components :as c]
   [hai.ui.components.form.core :as form]
   [hai.ui.components.form.input :as input]
   [hai.ui.components.form.select.single-select :as single-select]
   [hai.tags.combobox :as tag-combobox]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.web-app.components.form :as form-old]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private form-schema
  [:map
   [:contact/uuid {:identity? true} :uuid]
   [:contact/full-name
    {:title "Name"
     :form.field/placeholder "Full name"
     :optional true}
    :string]
   [:contact/email
    {:title "Emails"
     :optional true}
    [:vector :string]]
   [:contact/github-login
    {:title "GitHub username"
     :form.field/placeholder "octocat"
     :optional true}
    :string]
   [:contact/linkedin-url
    {:title "LinkedIn URL"
     :form.field/placeholder "https://linkedin.com/in/…"
     :optional true}
    :string]
   [:contact/type
    {:title "Type"
     :form.field/placeholder "Type"
     :form.field.select/options [{:value :contact.type/individual   :title "Individual"}
                                 {:value :contact.type/organisation :title "Organisation"}]
     :optional true}
    [:enum :contact.type/individual :contact.type/organisation]]
   [:contact/org-mailing-namespace
    {:title "Mailing domains"
     :optional true}
    [:vector :string]]])

(def ^:private close-icon (icons/mui-icon :outlined :close))

(o/defstyled removable-input :div
  :flex :flex-row :gap-2 :items-center
  [:>:first-child :flex-1]
  ([{:form.field.collection/keys [remove-self!] :as field}]
   [:<>
    [input/field field]
    [c/Button
     {:on-click (fn [_] (remove-self!))
      :variant "ghost"
      :size "1"
      :color "gray"
      :type "button"}
     [close-icon]]]))

(o/defstyled render :div
  :flex :flex-col :gap-3 :w-100
  [:.row :flex :flex-col :gap-1]
  [:.buttons :flex :flex-row :gap-3 :mt-2]
  ([{:form/keys [fields valid? on-submit submitting? dirty?]}
    {:keys [submit-label on-cancel tags-field tags-dirty?] :or {submit-label "Save"}}]
   (let [{:contact/keys [full-name email github-login linkedin-url type
                         org-mailing-namespace]}
         fields]
     [:<>
      [:div.row [form/label full-name]    [input/field full-name]]
      [:div.row [form/label email]        [form/list-of removable-input email]]
      [:div.row [form/label type]         [single-select/field type]]
      [:div.row [form/label github-login] [input/field github-login]]
      [:div.row [form/label linkedin-url] [input/field linkedin-url]]
      (when (= :contact.type/organisation (:form.field/current-value type))
        [:div.row [form/label org-mailing-namespace]
         [form/list-of removable-input org-mailing-namespace]])
      (when tags-field
        [:div.row [:label "Tags"] tags-field])
      [:div.buttons
       [c/Button
        {:disabled (or (not valid?) submitting? (and (not dirty?) (not tags-dirty?)))
         :on-click on-submit
         :size "2"} submit-label]
       (when on-cancel
         [c/Button
          {:on-click on-cancel
           :disabled submitting?
           :size "2"
           :color "crimson"
           :variant "outline"} "Cancel"])]])))

(defn- ->vec-field
  [m k]
  (cond-> m (contains? m k) (update k #(vec (or % [])))))

(defn- normalize-initial
  "A resolved (edit) contact doesn't match `form-schema`: cardinality-many fields
   come back as sets (schema wants vectors), `:contact/type` is a `{:db/ident …}`
   ref (schema wants the enum keyword), and absent fields arrive as explicit nils
   (an optional `:string` rejects nil). Normalize so the form validates and Save
   enables. Harmless for the create path (clean values stay clean)."
  [m]
  (let [m (-> m
              (->vec-field :contact/email)
              (->vec-field :contact/org-mailing-namespace))
        m (cond-> m (map? (:contact/type m)) (update :contact/type :db/ident))]
    (into {} (remove (comp nil? val)) m)))

(o/defstyled contact-form form-old/form
  ([{:keys [form-id initial-value initial-tags on-success-fx on-cancel submit-label]
     :or   {form-id ::form
            initial-value {}}}]
   (let [mutation (if (contains? initial-value :contact/uuid)
                    'contact/update
                    'contact/add-person)]
     (r/with-let [!tags (r/atom {:tag-uuids (vec (keep :group/uuid initial-tags))
                                 :tag-names []})]
       [form/component
        {:id            form-id
         :render        (fn [props]
                          ;; Tags live outside the form (the `!tags` atom), so the form's
                          ;; own `:form/dirty?` doesn't see tag edits. OR in a tag diff so a
                          ;; tag-only change still enables Save. Dereffing `!tags` here makes
                          ;; the button reactive to combobox changes.
                          [render props {:on-cancel    on-cancel
                                         :submit-label submit-label
                                         :tags-dirty?  (let [{:keys [tag-uuids tag-names]} @!tags
                                                             initial-uuids (set (keep :group/uuid initial-tags))]
                                                         (boolean (or (seq tag-names)
                                                                      (not= initial-uuids (set tag-uuids)))))
                                         :tags-field   (when @(<s :feature-flag/beta-features?)
                                                         [tag-combobox/combobox
                                                          {:on-change        #(reset! !tags %)
                                                           :initial-selected initial-tags
                                                           :placeholder      "Add tags..."}])}])
         :schema        form-schema
         :initial-value (normalize-initial
                         (merge {:contact/uuid (random-uuid)
                                 :contact/type :contact.type/individual}
                                initial-value))
         :on-submit     (fn [contact]
                          (let [{:keys [tag-uuids tag-names]} @!tags
                                initial-uuids (set (keep :group/uuid initial-tags))
                                untag-uuids   (vec (set/difference initial-uuids (set tag-uuids)))
                                contact (cond-> contact
                                          (seq tag-uuids)   (assoc :contact/tag-uuids tag-uuids)
                                          (seq tag-names)   (assoc :contact/tag-names tag-names)
                                          (seq untag-uuids) (assoc :contact/untag-uuids untag-uuids))]
                            (>e [:resolve [(list mutation contact)]
                                 {:on-success-fx (into [[:dispatch [:form/submitted form-id]]]
                                                       (or on-success-fx []))}])))}]))))
